import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useIsMomMode } from '../hooks/useIsMomMode'

/**
 * Routes the user to the correct mode based on detected identity.
 *
 *   requireRole="owner"     -> renders children for owners; redirects
 *                              consignee-only users to /mom.
 *   requireRole="consignee" -> renders children for consignees;
 *                              redirects owners back to '/'.
 *
 * Security note: this is UI sugar. RLS keeps mom out of owner data
 * even if she somehow navigates to an owner URL.
 */
export default function MomModeGuard({
  requireRole,
  children,
}: {
  requireRole: 'owner' | 'consignee'
  children: ReactNode
}) {
  const { loading, isMomMode } = useIsMomMode()
  const location = useLocation()

  if (loading) {
    return (
      <div style={{ padding: 'var(--space-8)', textAlign: 'center', color: 'var(--color-text-muted)' }}>
        Loading…
      </div>
    )
  }

  if (requireRole === 'owner' && isMomMode) {
    return <Navigate to="/mom" replace state={{ from: location }} />
  }
  if (requireRole === 'consignee' && !isMomMode) {
    return <Navigate to="/" replace state={{ from: location }} />
  }
  return <>{children}</>
}
